import { Assets, Rectangle, Sprite, Texture } from 'pixi.js';
import { prepareAirportGraphics } from './graphics.js';
import { cameraQuarter, DIRECTIONS, projectDirection } from './camera.js';
import { resolvePrimitive, primitiveCatalog } from './jet-set-catalog.js';
import { peopleAtlases } from './people.js';
import { ATLAS_FRAMES } from './atlas-frames.js';
import { staticFrames, staticArtBounds, staticFloorOrigins, staticDetailFloorOrigins } from './jet-set-frames.js';

const CELL = 256;
type Atlas = 'airport-props.png' | 'airport-people.png' | 'airport-details.png';
/** Size and anchor are given in the sprite's original 256px cell, so layout is independent of packing.
 *  `turn` rotates the object in the world by quarter turns: it is drawn with the art for that many camera
 *  quarters further round, since each direction's sheet shows every object from one side. */
type Placement = { atlas: Atlas; index: number; size: number; anchorX: number; anchorY: number; turn: number; grounded?: boolean };
const sprites = new Map<Sprite, Placement>();
const frames = new Map<string, Texture>();

function viewFor(turn = 0) { return DIRECTIONS[(cameraQuarter() + turn) % 4]; }

function path(file: string, direction = viewFor()) {
    return `/assets/art/${file === 'packed-airport-props.png' || file === 'packed-airport-details.png' ? 'jet-set' : 'directions'}/${direction}/${file}`;
}

export async function loadAirportArt() {
    await prepareAirportGraphics();
    await Assets.load([...DIRECTIONS.flatMap(direction => [
        path('packed-airport-props.png', direction), path('packed-airport-details.png', direction),
    ]), ...peopleAtlases(), ...['detector-arch', 'queue-post', 'boarding-bridge-deck', 'boarding-bridge-head', 'boarding-bridge-support', 'passenger-stairs'].flatMap(id => primitiveCatalog[id].views.map(view => view.path))]);
}

function frame(atlas: Atlas, index: number, turn: number) {
    if (atlas !== 'airport-people.png') return staticFrames[index] ?? [0, 0, 1, 1, 0, 0];
    return ATLAS_FRAMES[`${viewFor(turn)}/${atlas}`][index] ?? [0, 0, 1, 1, 0, 0];
}

function atlasTexture(atlasName: Atlas, index: number, turn: number) {
    const asset = path('packed-' + atlasName, viewFor(turn)), key = `${asset}:${index}`;
    const cached = frames.get(key);
    if (cached) return cached;
    const [x, y, w, h] = frame(atlasName, index, turn);
    const texture = new Texture(Texture.from(asset).baseTexture, new Rectangle(x, y, w, h));
    frames.set(key, texture);
    return texture;
}

/** Applies the current camera's frame: the packed box keeps its offset inside the original cell. */
function place(image: Sprite, p: Placement) {
    const [, , w, h, offsetX, offsetY] = frame(p.atlas, p.index, p.turn);
    image.texture = atlasTexture(p.atlas, p.index, p.turn);
    const origins = p.atlas === 'airport-details.png' ? staticDetailFloorOrigins : staticFloorOrigins;
    const origin = p.grounded ? origins[p.index][(cameraQuarter() + p.turn) % 4] : [p.anchorX * CELL, p.anchorY * CELL];
    image.anchor.set((origin[0] - offsetX) / w, (origin[1] - offsetY) / h);
    image.scale.set(p.size / CELL);
}

function atlasSprite(atlas: Atlas, index: number, size: number, anchorX: number, anchorY: number, turn = 0, grounded = false): Sprite {
    const image = new Sprite();
    const placement = { atlas, index, size, anchorX, anchorY, turn, grounded };
    place(image, placement);
    sprites.set(image, placement);
    image.on('destroyed', () => sprites.delete(image));
    return image;
}

const primitives = new Map<Sprite, { id: string; size: number; turn: number }>();

function placePrimitive(image: Sprite, id: string, size: number, turn: number) {
    const { view, views } = resolvePrimitive(id, cameraQuarter(), turn);
    image.texture = Texture.from(view.path);
    image.anchor.set(view.anchor[0] / view.width, view.anchor[1] / view.height);
    image.scale.set(size / Math.max(...views.map(v => Math.max(v.bounds[2] - v.bounds[0], v.bounds[3] - v.bounds[1]))));
}

/** Model views rotate with the camera; assets without a model retain one base image. */
export function primitive(id: string, size: number, turn = 0): Sprite {
    const image = new Sprite();
    placePrimitive(image, id, size, turn);
    primitives.set(image, { id, size, turn });
    image.on('destroyed', () => primitives.delete(image));
    return image;
}

export function refreshDirectionalSprites() {
    for (const [image, placement] of sprites) place(image, placement);
    for (const [image, p] of primitives) placePrimitive(image, p.id, p.size, p.turn);
}

export function prop(index: number, size: number, turn = 0): Sprite {
    return atlasSprite('airport-props.png', index, size, 0.5, 1, turn);
}

export function groundedProp(index: number, size: number, turn = 0): Sprite {
    return atlasSprite('airport-props.png', index, size, 0.5, 1, turn, true);
}

/** Mouse targets follow the visible model, excluding padding in the atlas cell. */
export function propHitArea(index: number, size: number, turn = 0, grounded = false) {
    const [left, top, right, bottom] = staticArtBounds[viewFor(turn)][index], scale = size / CELL;
    const origin = grounded ? staticFloorOrigins[index][(cameraQuarter() + turn) % 4] : [CELL / 2, CELL];
    return new Rectangle((left - origin[0]) * scale, (top - origin[1]) * scale, (right - left) * scale, (bottom - top) * scale);
}

export function detail(index: number, size: number, turn = 0): Sprite {
    return atlasSprite('airport-details.png', index, size, 0.5, 1, turn);
}

export function groundedDetail(index: number, size: number, turn = 0): Sprite {
    return atlasSprite('airport-details.png', index, size, 0.5, 1, turn, true);
}

/** Change a prop orientation while retaining its atlas placement and floor anchor. */
export function turnProp(image: Sprite, turn: number) {
    const placement = sprites.get(image);
    if (!placement || placement.turn === turn) return;
    placement.turn = turn;
    place(image, placement);
}

/** Aircraft turns follow their world heading; fixed placeholders retain one image. */
export function faceAircraft(image: Sprite, dx: number, dy: number) {
    // The aircraft model's views point its nose a quarter turn round from the direction they are filed under.
    const screen = projectDirection(-dy, dx);
    const view = screen.y < 0 ? screen.x > 0 ? 3 : 0 : screen.x > 0 ? 2 : 1;
    turnProp(image, (view - cameraQuarter() + 4) % 4);
}
